/**
 * AskAIModal.js
 *
 * Modal component for composing a question to the AI Agent about chart drawings.
 *
 * WHY THIS EXISTS:
 * - Users need to provide context when asking about marked chart areas
 * - Modal provides a clear interface for composing their question
 * - Summarizes the drawings so users know what will be sent
 * - Viewport and drawing coordinates are sent as structured data (no screenshots)
 *
 * FLOW:
 * 1. User clicks "Ask AI" in the draw toolbar
 * 2. This modal opens with a summary of their drawings
 * 3. User types a question or leaves blank for a general analysis
 * 4. User submits -> navigates to AI Agent with viewport + drawing coordinate data
 */
import React, { useState } from 'react';
import { FaTimes, FaMapPin, FaCircle, FaArrowRight, FaSquare, FaMinus, FaFont, FaRobot } from 'react-icons/fa';
import './AskAIModal.css';

/**
 * Get the appropriate icon for a drawing type.
 */
const getDrawingIcon = (type) => {
  switch (type) {
    case 'pin': return FaMapPin;
    case 'circle': return FaCircle;
    case 'arrow': return FaArrowRight;
    case 'rectangle': return FaSquare;
    case 'line': return FaMinus;
    case 'text': return FaFont;
    default: return FaMapPin;
  }
};

/**
 * Get human-readable label for a drawing type.
 */
const getDrawingLabel = (type) => {
  switch (type) {
    case 'pin': return 'Pin';
    case 'circle': return 'Circle';
    case 'arrow': return 'Arrow';
    case 'rectangle': return 'Rectangle';
    case 'line': return 'Line';
    case 'text': return 'Text';
    default: return 'Drawing';
  }
};

/**
 * AskAIModal - Modal for composing questions about chart drawings.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Array} props.drawings - Array of drawing objects from the chart
 * @param {Function} props.onSubmit - Callback when user submits their question
 *   Called with { question: string }
 */
export function AskAIModal({
  isOpen,
  onClose,
  drawings = [],
  onSubmit,
}) {
  const [question, setQuestion] = useState('');

  // Don't render if not open
  if (!isOpen) return null;

  // Count drawings by type
  const drawingCounts = drawings.reduce((acc, drawing) => {
    acc[drawing.type] = (acc[drawing.type] || 0) + 1;
    return acc;
  }, {});

  // Get text annotations for display
  const textAnnotations = drawings.filter(d => d.type === 'text' && d.text);

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      question: question.trim(),
    });
  };

  // Handle escape key
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div
      className="ask-ai-modal-overlay"
      onClick={onClose}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="ask-ai-modal-title"
    >
      <div
        className="ask-ai-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="ask-ai-modal-header">
          <div className="ask-ai-modal-title-row">
            <FaRobot className="ask-ai-modal-icon" />
            <h3 id="ask-ai-modal-title" className="ask-ai-modal-title">
              Ask AI About Your Markings
            </h3>
          </div>
          <button
            className="ask-ai-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            <FaTimes />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="ask-ai-modal-content">
          {/* Drawing summary */}
          <div className="ask-ai-modal-summary">
            <span className="ask-ai-modal-summary-label">
              Sending {drawings.length} marking{drawings.length !== 1 ? 's' : ''} to AI:
            </span>
            <div className="ask-ai-modal-drawings">
              {Object.entries(drawingCounts).map(([type, count]) => {
                const Icon = getDrawingIcon(type);
                return (
                  <span key={type} className="ask-ai-modal-drawing-chip">
                    <Icon className="ask-ai-modal-drawing-icon" />
                    <span>{count} {getDrawingLabel(type)}{count !== 1 ? 's' : ''}</span>
                  </span>
                );
              })}
            </div>
            {/* Show text annotations if any */}
            {textAnnotations.length > 0 && (
              <div className="ask-ai-modal-text-annotations">
                <span className="ask-ai-modal-text-label">Text annotations:</span>
                <ul className="ask-ai-modal-text-list">
                  {textAnnotations.slice(0, 3).map((d, i) => (
                    <li key={d.id || i} className="ask-ai-modal-text-item">
                      "{d.text.length > 40 ? d.text.substring(0, 40) + '...' : d.text}"
                    </li>
                  ))}
                  {textAnnotations.length > 3 && (
                    <li className="ask-ai-modal-text-more">
                      +{textAnnotations.length - 3} more
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>

          {/* Question input */}
          <div className="ask-ai-modal-question-group">
            <label htmlFor="ask-ai-question" className="ask-ai-modal-label">
              What would you like to know?
              <span className="ask-ai-modal-optional">(optional)</span>
            </label>
            <textarea
              id="ask-ai-question"
              className="ask-ai-modal-textarea"
              placeholder="E.g., What happened in this area? Why did the price drop here? Is this a good entry point?"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              rows={3}
              autoFocus
            />
            <p className="ask-ai-modal-hint">
              Leave blank to ask for a general analysis of the marked areas.
            </p>
          </div>

          {/* Actions */}
          <div className="ask-ai-modal-actions">
            <button
              type="button"
              className="ask-ai-modal-btn ask-ai-modal-cancel"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="ask-ai-modal-btn ask-ai-modal-submit"
            >
              <FaRobot />
              Ask AI Agent
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AskAIModal;
